import { __assign } from "tslib";
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { getContainingBlock, nodeContains } from '../internal/utils/dom';
import { useContainerQuery } from '../internal/hooks/container-queries';
import { calculatePosition } from './utils/positions';
import styles from './styles.css.js';
var INITIAL_STYLES = { position: 'absolute', top: -9999, left: -9999 };
export default function PopoverContainer(_a) {
    var position = _a.position, track = _a.track, arrow = _a.arrow, children = _a.children, zIndex = _a.zIndex;
    var _b = useContainerQuery(function (rect, prev) {
        var roundedRect = { width: Math.round(rect.width), height: Math.round(rect.height) };
        return (prev === null || prev === void 0 ? void 0 : prev.width) === roundedRect.width && (prev === null || prev === void 0 ? void 0 : prev.height) === roundedRect.height ? prev : rect;
    }), popoverRect = _b[0], ref = _b[1];
    var bodyRef = useRef(null);
    var arrowRef = useRef(null);
    var onMount = useCallback(function (element) {
        bodyRef.current = element;
        ref.current = element;
    }, [ref]);
    var _c = useState(INITIAL_STYLES), inlineStyle = _c[0], setInlineStyle = _c[1];
    var _d = useState(null), internalPosition = _d[0], setInternalPosition = _d[1];
    useLayoutEffect(function () {
        var positionBody = function () {
            if (!ref.current) {
                return function () { };
            }
            var document = ref.current.ownerDocument;
            if (!track || !nodeContains(document.body, track) || !bodyRef.current || !arrowRef.current) {
                return function () { };
            }
            // Get important elements
            var body = bodyRef.current;
            var arrow = arrowRef.current;
            var containingBlock = getContainingBlock(body);
            // If the popover body isn't being rendered for whatever reason
            // (e.g. "display: none" or JSDOM), bail on calculating dimensions.
            if (body.offsetWidth === 0 || body.offsetHeight === 0) {
                return function () { };
            }
            // Imperatively move body off-screen to give it room to expand.
            // Not doing this in React because this recalculation should happen
            // in the span of a single frame without rerendering anything.
            var prevTop = body.style.top;
            var prevLeft = body.style.left;
            body.style.top = '0';
            body.style.left = '0';
            // Get rects representing key elements
            // Use getComputedStyle for arrowRect to avoid modifications made by transform
            var viewportRect = getViewportRect(document.defaultView);
            var trackRect = track.getBoundingClientRect();
            var arrowRect = {
                width: parseFloat(getComputedStyle(arrow).width),
                height: parseFloat(getComputedStyle(arrow).height)
            };
            var containingBlockRect = containingBlock ? containingBlock.getBoundingClientRect() : viewportRect;
            // Round up dimensions (IE11 doesn't handle subpixels too accurately)
            var bodyRect = body.getBoundingClientRect();
            var bodyRectCeil = {
                top: bodyRect.top,
                left: bodyRect.left,
                width: Math.ceil(bodyRect.width),
                height: Math.ceil(bodyRect.height)
            };
            // Calculate the arrow direction and viewport-relative position of the popover.
            var _a = calculatePosition(position, trackRect, arrowRect, bodyRectCeil, containingBlock ? containingBlockRect : getDocumentRect(document), viewportRect), newInternalPosition = _a.internalPosition, boundingOffset = _a.boundingOffset;
            // Get the position of the popover relative to the offset parent.
            var popoverOffset = toRelativePosition(boundingOffset, containingBlockRect);
            // Cache the distance between the trigger and the popover (which stays the same as you scroll),
            // and use that to recalculate the new popover position.
            var trackRelativeOffset = toRelativePosition(popoverOffset, toRelativePosition(trackRect, containingBlockRect));
            // Bring back the container to its original position to prevent any
            // flashing.
            body.style.top = prevTop;
            body.style.left = prevLeft;
            // Position the popover
            setInternalPosition(newInternalPosition);
            setInlineStyle({ top: popoverOffset.top, left: popoverOffset.left });
            return function () {
                var newTrackOffset = toRelativePosition(track.getBoundingClientRect(), containingBlock ? containingBlock.getBoundingClientRect() : viewportRect);
                setInlineStyle({
                    top: newTrackOffset.top + trackRelativeOffset.top,
                    left: newTrackOffset.left + trackRelativeOffset.left
                });
            };
        };
        // This callback is stored in an object property so that it can still be replaced from outside the listener closure.
        var onScroll = { callback: positionBody() };
        var onScrollListener = function () { return onScroll.callback(); };
        var onClick = function () {
            return requestAnimationFrame(function () {
                onScroll.callback = positionBody();
            });
        };
        window.addEventListener('resize', positionBody);
        window.addEventListener('scroll', onScrollListener, true);
        /*
          This is a heuristic. Some layout changes are caused by user clicks (e.g. toggling the tools panel, submitting a form),
          and by tracking the click event we can adapt the popover's position to the new layout.
        */
        // TODO extend this to Enter and Spacebar?
        window.addEventListener('click', onClick);
        return function () {
            window.removeEventListener('resize', positionBody);
            window.removeEventListener('scroll', onScrollListener, true);
            window.removeEventListener('click', onClick);
        };
    }, [position, track, popoverRect, ref]);
    return (React.createElement("div", { ref: onMount, style: __assign(__assign({}, inlineStyle), { zIndex: zIndex }), className: styles.container },
        React.createElement("div", { ref: arrowRef, className: clsx(styles["container-arrow"], styles["container-arrow-position-" + internalPosition]), "aria-hidden": true }, arrow(internalPosition)),
        children));
}
/**
 * Convert a viewport-relative offset to an element-relative offset.
 */
function toRelativePosition(element, parent) {
    return {
        top: element.top - parent.top,
        left: element.left - parent.left
    };
}
/**
 * Get a BoundingOffset that represents the visible viewport.
 */
function getViewportRect(window) {
    return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight
    };
}
function getDocumentRect(document) {
    var _a = document.documentElement.getBoundingClientRect(), top = _a.top, left = _a.left;
    return {
        top: top,
        left: left,
        width: document.documentElement.scrollWidth,
        height: document.documentElement.scrollHeight
    };
}
//# sourceMappingURL=container.js.map